/**
 * PosLoader — macOS / iOS / Wolt tarzı dairesel spinner.
 *
 * İnce bir turuncu yay (üçte biri kadar) sabit hızda döner,
 * arkasında çok hafif solğun bir tam halka vardır. Apple Activity
 * Indicator'a yakın bir görünüm; tek bir CSS `transform: rotate`
 * üzerinden çalışır — compositor-only, paint tetiklemez,
 * sayfanın geri kalanını hiç etkilemez.
 *
 * Uygulamanın her sayfasında aynı spinner; tutarlı görünüm.
 *
 * Kullanım:
 *   <PosLoader />                              // varsayılan, inline
 *   <PosLoader size="sm" />                    // küçük (button vb. için)
 *   <PosLoader size="lg" label="Yükleniyor" />// büyük + alt metin
 *   <PosLoader fullscreen />                   // tam ekran overlay
 */
import React from 'react';

type Size = 'sm' | 'md' | 'lg' | 'xl';

interface Props {
  /** Çap — sm=20px (button), md=36px (default), lg=52px (page), xl=72px (splash) */
  size?: Size;
  /** Altında ipucu metni */
  label?: string;
  /** true => fullscreen overlay (sticky merkez), false => inline */
  fullscreen?: boolean;
  /** Spinner rengi; default turuncu (#ff8a00) */
  color?: string;
  /** Yay kalınlığı override */
  thickness?: number;
  className?: string;
}

const sizeMap: Record<Size, { d: number; t: number; text: string }> = {
  sm: { d: 20, t: 2,   text: 'text-[10px]' },
  md: { d: 36, t: 3,   text: 'text-xs'      },
  lg: { d: 52, t: 3.5, text: 'text-sm'      },
  xl: { d: 72, t: 4,   text: 'text-base'    },
};

export const PosLoader: React.FC<Props> = ({
  size = 'md',
  label,
  fullscreen = false,
  color = '#ff8a00',
  thickness,
  className = '',
}) => {
  const s = sizeMap[size];
  const t = thickness ?? s.t;

  const wrapperClass = fullscreen
    ? 'fixed inset-0 z-[200] flex flex-col items-center justify-center bg-white/60 dark:bg-[#0f1117]/70 backdrop-blur-[2px]'
    : 'flex flex-col items-center justify-center';

  return (
    <div
      className={`${wrapperClass} ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {/*
        SVG dairesel spinner — tek `<circle>`'ın stroke-dasharray ile
        üçte birini gösterip kontener'ı döndürüyoruz. Native macOS
        Activity Indicator'a göre daha modern, Wolt'tan tanıdık.
      */}
      <svg
        className="pos-spinner"
        width={s.d}
        height={s.d}
        viewBox="0 0 50 50"
        aria-hidden="true"
        focusable="false"
      >
        {/* Solğun arka halka */}
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke={color}
          strokeOpacity="0.16"
          strokeWidth={t}
        />
        {/* Aktif yay (üçte bir tur) */}
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke={color}
          strokeWidth={t}
          strokeLinecap="round"
          strokeDasharray="40 200"
        />
      </svg>

      {label && (
        <span
          className={`mt-3 ${s.text} font-medium text-gray-500 dark:text-gray-400 tracking-wide`}
        >
          {label}
        </span>
      )}

      <span className="sr-only">Yükleniyor</span>
    </div>
  );
};

export default PosLoader;
